import { useNavigate } from 'react-router-dom';
import {
  Users, CheckCircle, XCircle, TrendingUp, DollarSign,
  AlertCircle, RefreshCw, Zap,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { CreditLevel } from '@/types/credit.types';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { CompactStat } from '@/components/data-display/CompactStat';
import { ROUTES } from '@/constants';

import { useScoring } from '../../hooks/useScoring';
import { formatCurrency, formatNumber } from '@/utils/formatters';
import type { ProfileSummaryResponse } from '@/types/scoring.types';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Originacion', path: ROUTES.PIPELINE_MATRIX },
  { label: 'Scoring', path: ROUTES.SCORING },
];

// ─── Level helpers ──────────────────────────────────────────────────────────

const LEVEL_ORDER = ['PLATINUM', 'GOLD', 'SILVER', 'BRONZE', 'NONE'];

// Bar colors matching CreditLevelBadge
const LEVEL_COLORS: Record<string, { bg: string; text: string; bar: string }> = {
  PLATINUM: { bg: 'bg-blue-500/10', text: 'text-blue-400', bar: 'bg-blue-500' },
  GOLD: { bg: 'bg-amber-500/10', text: 'text-amber-400', bar: 'bg-[#DAA520]' },
  SILVER: { bg: 'bg-slate-400/10', text: 'text-slate-400', bar: 'bg-[#94A3B8]' },
  BRONZE: { bg: 'bg-orange-600/10', text: 'text-orange-500', bar: 'bg-[#CD7F32]' },
  NONE: { bg: 'bg-gray-400/10', text: 'text-gray-500', bar: 'bg-gray-400' },
};

function getLevelStyle(level: string) {
  return LEVEL_COLORS[level] ?? { bg: 'bg-gray-400/10', text: 'text-gray-500', bar: 'bg-gray-400' };
}

// ─── Summary KPIs ───────────────────────────────────────────────────────────

type KpiTarget = 'all' | 'eligible' | 'ineligible' | 'score' | 'credit';

function SummaryKPIs({
  summary,
  isLoading,
  onNavigate,
}: {
  summary: ProfileSummaryResponse | null;
  isLoading: boolean;
  onNavigate: (target: KpiTarget) => void;
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} height={56} variant="rectangular" />
        ))}
      </div>
    );
  }

  const total = summary?.total_profiles ?? 0;
  const eligible = summary?.eligible_count ?? 0;
  const ineligible = summary?.ineligible_count ?? 0;
  const avgScore = summary?.avg_score ?? 0;
  const avgCredit = summary?.avg_max_credit_mxn ?? 0;
  const eligiblePct = total > 0 ? `${((eligible / total) * 100).toFixed(1)}%` : '0%';
  const ineligiblePct = total > 0 ? `${((ineligible / total) * 100).toFixed(1)}%` : '0%';

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <CompactStat
        title="Total Perfiles"
        value={formatNumber(total)}
        icon={Users}
        onClick={() => onNavigate('all')}
      />
      <CompactStat
        title="Elegibles"
        value={formatNumber(eligible)}
        subtitle={eligiblePct}
        icon={CheckCircle}
        iconAccent="green"
        onClick={() => onNavigate('eligible')}
      />
      <CompactStat
        title="No Elegibles"
        value={formatNumber(ineligible)}
        subtitle={ineligiblePct}
        icon={XCircle}
        iconAccent="red"
        onClick={() => onNavigate('ineligible')}
      />
      <CompactStat
        title="Score Promedio"
        value={avgScore.toFixed(1)}
        icon={TrendingUp}
        onClick={() => onNavigate('score')}
      />
      <CompactStat
        title="Credito Promedio"
        value={formatCurrency(avgCredit, 'MXN', 'es-MX')}
        icon={DollarSign}
        onClick={() => onNavigate('credit')}
      />
    </div>
  );
}

// ─── Level Distribution (visual cards) ──────────────────────────────────────

function LevelDistribution({ summary }: { summary: ProfileSummaryResponse | null }) {
  const levels = summary?.by_level;
  if (!levels || levels.length === 0) return null;

  const sorted = [...levels].sort((a, b) =>
    (LEVEL_ORDER.indexOf(a.level) === -1 ? 99 : LEVEL_ORDER.indexOf(a.level)) -
    (LEVEL_ORDER.indexOf(b.level) === -1 ? 99 : LEVEL_ORDER.indexOf(b.level))
  );
  const maxCount = Math.max(...sorted.map((l) => l.count), 1);

  return (
    <Card title="Distribucion por Nivel" description="Desglose de perfiles por nivel crediticio">
      <div className="space-y-3">
        {sorted.map((item) => {
          const style = getLevelStyle(item.level);
          const pct = ((item.count / (summary?.total_profiles ?? 1)) * 100).toFixed(1);
          return (
            <div key={item.level} className="flex items-center gap-4">
              <div className="w-20 shrink-0">
                {Object.values(CreditLevel).includes(item.level as CreditLevel)
                  ? <CreditLevelBadge level={item.level as CreditLevel} size="sm" />
                  : <span className="text-xs text-muted-foreground">{item.level}</span>
                }
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-7 rounded-md bg-muted dark:bg-slate-700/50 overflow-hidden">
                    <div
                      className={`h-full rounded-md ${style.bar} transition-all duration-500`}
                      style={{ width: `${(item.count / maxCount) * 100}%`, minWidth: item.count > 0 ? '8px' : '0' }}
                    />
                  </div>
                  <span className="w-10 text-right text-sm font-semibold tabular-nums text-foreground">
                    {item.count}
                  </span>
                  <span className="w-14 text-right text-xs tabular-nums text-muted-foreground">
                    {pct}%
                  </span>
                </div>
              </div>
              <div className="hidden sm:flex gap-4 shrink-0">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Elegibles</p>
                  <p className="text-sm font-semibold tabular-nums text-green-600">{item.eligible}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">No Eleg.</p>
                  <p className="text-sm font-semibold tabular-nums text-red-500">{item.ineligible}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Score Distribution Histogram ───────────────────────────────────────────

// Bar color by score range: higher score = better color (matches level logic)
// Blue gradient: higher score = more intense
const SCORE_BAR_COLORS: Record<string, string> = {
  '80-100': 'bg-blue-500',
  '70-80': 'bg-blue-400',
  '60-70': 'bg-sky-400',
  '50-60': 'bg-sky-300',
  '25-50': 'bg-slate-400',
  '0-25': 'bg-slate-500',
};

function getScoreBarColor(range: string): string {
  return SCORE_BAR_COLORS[range] ?? 'bg-primary/80';
}

function ScoreDistribution({ summary }: { summary: ProfileSummaryResponse | null }) {
  const buckets = summary?.score_distribution;
  if (!buckets || buckets.length === 0) return null;

  // Sort high to low (same direction as levels: best first)
  const sorted = [...buckets].sort((a, b) => {
    const numA = parseInt(a.range.split('-')[0] ?? '0', 10);
    const numB = parseInt(b.range.split('-')[0] ?? '0', 10);
    return numB - numA;
  });
  const maxCount = Math.max(...sorted.map((b) => b.count), 1);
  const totalProfiles = sorted.reduce((sum, b) => sum + b.count, 0);

  return (
    <Card title="Distribucion de Scores" description="Histograma de puntajes crediticios (mayor a menor)">
      <div className="space-y-3">
        {sorted.map(({ range, count }) => {
          const pct = totalProfiles > 0 ? ((count / totalProfiles) * 100).toFixed(0) : '0';
          const barColor = getScoreBarColor(range);
          return (
            <div key={range} className="flex items-center gap-3">
              <span className="w-14 shrink-0 text-right text-xs font-mono text-muted-foreground">
                {range}
              </span>
              <div className="flex-1 h-7 rounded-md bg-muted dark:bg-slate-700/50 overflow-hidden">
                <div
                  className={`h-full rounded-md ${barColor} transition-all duration-500`}
                  style={{
                    width: `${(count / maxCount) * 100}%`,
                    minWidth: count > 0 ? '6px' : '0px',
                  }}
                />
              </div>
              <span className="w-8 text-right text-sm font-semibold tabular-nums text-foreground">
                {count}
              </span>
              <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">
                {pct}%
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Pipeline Summary ───────────────────────────────────────────────────────

function PipelineSummaryCard({ summary }: { summary: ProfileSummaryResponse | null }) {
  const pipeline = summary?.pipeline;
  if (!pipeline) return null;

  return (
    <Card title="Pipeline de Credito" description="Potencial de originacion basado en perfiles elegibles">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <CompactStat
          title="Elegibles en Pipeline"
          value={formatNumber(pipeline.eligible_count)}
          icon={CheckCircle}
          iconAccent="green"
        />
        <CompactStat
          title="Potencial Total"
          value={formatCurrency(pipeline.total_potential_mxn, 'MXN', 'es-MX')}
          icon={DollarSign}
          iconAccent="green"
        />
        <CompactStat
          title="Potencial Promedio"
          value={formatCurrency(pipeline.avg_potential_mxn, 'MXN', 'es-MX')}
          icon={DollarSign}
        />
      </div>

      {pipeline.by_banner_state && pipeline.by_banner_state.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Estado del Banner
          </p>
          <div className="flex flex-wrap gap-1.5">
            {pipeline.by_banner_state.map((s) => (
              <div
                key={s.state}
                className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1"
              >
                <Zap size={12} className="text-primary" />
                <span className="text-xs font-medium text-foreground">
                  {s.state.replace(/_/g, ' ')}
                </span>
                <Badge variant="default">{s.count}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {pipeline.by_level && pipeline.by_level.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Potencial por Nivel
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-2.5 py-1.5 font-medium text-muted-foreground">Nivel</th>
                  <th className="px-2.5 py-1.5 text-right font-medium text-muted-foreground">Perfiles</th>
                  <th className="px-2.5 py-1.5 text-right font-medium text-muted-foreground">Total Potencial</th>
                  <th className="px-2.5 py-1.5 text-right font-medium text-muted-foreground">Promedio</th>
                </tr>
              </thead>
              <tbody>
                {pipeline.by_level.map((pl) => (
                  <tr key={pl.level} className="border-b border-border/50">
                    <td className="px-2.5 py-1.5">
                      {Object.values(CreditLevel).includes(pl.level as CreditLevel) ? (
                        <CreditLevelBadge level={pl.level as CreditLevel} size="sm" />
                      ) : (
                        <span className="text-xs text-muted-foreground">{pl.level}</span>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5 text-right tabular-nums text-foreground">{pl.count}</td>
                    <td className="px-2.5 py-1.5 text-right font-medium tabular-nums text-green-600">
                      {formatCurrency(pl.total_potential_mxn, 'MXN', 'es-MX')}
                    </td>
                    <td className="px-2.5 py-1.5 text-right tabular-nums text-foreground">
                      {formatCurrency(pl.avg_potential_mxn, 'MXN', 'es-MX')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── Loading & Error States ─────────────────────────────────────────────────

function ScoringLoading() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} height={96} variant="rectangular" />
        ))}
      </div>
      <Skeleton height={200} variant="rectangular" />
      <Skeleton height={300} variant="rectangular" />
    </div>
  );
}

function ScoringError({ onRetry }: { onRetry: () => void }) {
  return (
    <Card>
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <AlertCircle size={32} className="text-destructive" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Error al cargar datos</h3>
        <p className="max-w-md text-sm text-muted-foreground">
          No se pudieron cargar los datos de scoring. Verifica tu conexion e intenta de nuevo.
        </p>
        <Button variant="primary" onClick={onRetry}>
          Reintentar
        </Button>
      </div>
    </Card>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function ScoringPage() {
  const navigate = useNavigate();
  const { summary, isSummaryLoading, isLoading, isError, refetch } = useScoring();

  function handleKpiNavigate(target: KpiTarget) {
    switch (target) {
      case 'eligible':
        navigate(`${ROUTES.CREDIT_CANDIDATOS}?eligible=YES`);
        return;
      case 'ineligible':
        navigate(`${ROUTES.CREDIT_CANDIDATOS}?eligible=NO`);
        return;
      case 'score':
        navigate(`${ROUTES.CREDIT_CANDIDATOS}?sort=score&order=desc`);
        return;
      case 'credit':
        navigate(`${ROUTES.CREDIT_CANDIDATOS}?sort=credit&order=desc`);
        return;
      case 'all':
      default:
        navigate(ROUTES.CREDIT_CANDIDATOS);
    }
  }

  return (
    <div>
      <PageHeader
        title="Inteligencia de Perfiles Crediticios"
        helpKey="scoring"
        subtitle="Analisis de perfiles, niveles, elegibilidad y fuentes de ingreso"
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={refetch}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        {isLoading && <ScoringLoading />}
        {isError && !isLoading && <ScoringError onRetry={refetch} />}
        {!isLoading && !isError && (
          <>
            <SummaryKPIs summary={summary} isLoading={isSummaryLoading} onNavigate={handleKpiNavigate} />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <LevelDistribution summary={summary} />
              <ScoreDistribution summary={summary} />
            </div>
            <PipelineSummaryCard summary={summary} />
          </>
        )}
      </div>
    </div>
  );
}
